/**
 * Video decoding and presentation.
 *
 * RustDesk sends raw encoded bitstream frames (VP8/VP9/H264/H265/AV1). The
 * browser decodes them with WebCodecs and paints onto a canvas.
 *
 * Two constraints drive the design:
 *  - A decoder must start on a key frame. If the stream is joined mid-GOP the
 *    first frames are dropped and a refresh is requested, rather than feeding
 *    the decoder garbage.
 *  - Decoding is async and can fall behind. Frames are dropped (oldest first)
 *    when the queue grows, because for remote control latency matters far more
 *    than completeness.
 */
/** WebCodecs codec strings. */
const CODEC_STRINGS = {
    vp8: "vp8",
    // profile 0, level 1.0, 8-bit: what RustDesk's encoder produces.
    vp9: "vp09.00.10.08",
    // Baseline 3.0. The peer sends Annex-B, so no `description` is supplied.
    h264: "avc1.42E01E",
    h265: "hev1.1.6.L93.B0",
    av1: "av01.0.04M.08",
};
/**
 * Decoder backlog past which delta frames are dropped.
 *
 * `VideoDecoder.decodeQueueSize` is the real measure of whether the decoder is
 * keeping up. A small fixed cap counted in software throws away frames the
 * decoder could have handled, which shows up as a low frame rate rather than
 * as smooth playback.
 */
const MAX_PENDING = 16;
export async function isCodecSupported(codec) {
    if (typeof VideoDecoder === "undefined")
        return false;
    try {
        const support = await VideoDecoder.isConfigSupported({ codec: CODEC_STRINGS[codec] });
        return !!support.supported;
    }
    catch {
        return false;
    }
}
/** Codecs this browser can actually decode, for the login handshake. */
export async function detectCodecs() {
    const names = ["vp8", "vp9", "h264", "h265", "av1"];
    const out = {};
    await Promise.all(names.map(async (n) => {
        out[n] = await isCodecSupported(n);
    }));
    return out;
}
export class VideoSink {
    /*
     * Either kind of canvas.
     *
     * A screen shown in its own window is drawn into an OffscreenCanvas that
     * window handed over, so the frames never leave the one connection that
     * decoded them. The drawing surface is the only difference; everything
     * below treats them the same.
     */
    #canvas;
    #ctx;
    #decoder = null;
    #codec = null;
    #events;
    #sawKeyFrame = false;
    #pending = 0;
    #closed = false;
    #refreshRequested = false;
    #fpsWindow = [];
    /** Set as soon as configuration starts, not when it finishes. */
    #configuring = null;
    /** Frames that arrived while the decoder was still being configured. */
    #queued = [];
    stats = {
        codec: null, framesDecoded: 0, framesDropped: 0, keyFramesSeen: 0,
        width: 0, height: 0, lastFrameAt: 0, fps: 0,
    };
    constructor(canvas, events = {}) {
        this.#canvas = canvas;
        // `alpha: false` lets the compositor skip blending; `desynchronized` cuts
        // a frame of latency where the browser supports it.
        this.#ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
        this.#events = events;
    }
    get codec() {
        return this.#codec;
    }
    /** True when this codec is ready, or already being set up. */
    ready(codec) {
        return this.#configuring === codec ||
            (this.#codec === codec && this.#decoder?.state === "configured");
    }
    /** (Re)configures the decoder for a codec. Safe to call repeatedly. */
    async configure(codec) {
        if (this.ready(codec))
            return;
        // Claim the codec immediately: configuration awaits, and without this every
        // frame that arrives meanwhile would start a competing configure and tear
        // down the decoder that is being built.
        this.#configuring = codec;
        this.#teardownDecoder();
        if (typeof VideoDecoder === "undefined") {
            this.#events.onError?.("This browser does not support WebCodecs, which the remote viewer needs.");
            return;
        }
        const decoder = new VideoDecoder({
            output: (frame) => this.#paint(frame),
            error: (err) => {
                // A decoder error usually means a corrupt GOP; recover by asking for
                // a fresh key frame rather than tearing the session down.
                this.#sawKeyFrame = false;
                this.#requestRefresh();
                this.#events.onError?.(err.message);
            },
        });
        const config = {
            codec: CODEC_STRINGS[codec],
            optimizeForLatency: true,
        };
        try {
            const support = await VideoDecoder.isConfigSupported(config);
            if (!support.supported) {
                this.#configuring = null;
                this.#events.onError?.(`This browser cannot decode ${codec.toUpperCase()}.`);
                return;
            }
            decoder.configure(config);
        }
        catch (err) {
            this.#configuring = null;
            this.#events.onError?.(`Could not start the ${codec.toUpperCase()} decoder: ${err.message}`);
            return;
        }
        // A newer configure() superseded this one while we were awaiting.
        if (this.#configuring !== codec) {
            try {
                decoder.close();
            }
            catch { /* already gone */ }
            return;
        }
        this.#decoder = decoder;
        this.#codec = codec;
        this.stats.codec = codec;
        this.#sawKeyFrame = false;
        this.#configuring = null;
        // Drain anything that arrived during setup, so the first key frame is not
        // lost and playback starts immediately rather than after the next one.
        const queued = this.#queued.splice(0);
        for (const f of queued)
            this.decode(f.data, f.key, f.pts);
    }
    /** Feeds one encoded frame. `pts` is the peer's timestamp in milliseconds. */
    decode(data, key, pts) {
        if (this.#closed)
            return;
        const decoder = this.#decoder;
        if (!decoder || decoder.state !== "configured") {
            // Still configuring: hold a key frame so playback can start the moment
            // the decoder is ready. Delta frames are useless without it.
            if (this.#configuring && key) {
                this.#queued = [{ data, key, pts }];
            }
            return;
        }
        if (!this.#sawKeyFrame) {
            if (!key) {
                // Joined mid-GOP: a delta frame has no reference to decode against.
                this.stats.framesDropped++;
                this.#requestRefresh();
                return;
            }
            this.#sawKeyFrame = true;
            this.#refreshRequested = false;
        }
        if (key)
            this.stats.keyFramesSeen++;
        const backlog = Math.max(this.#pending, decoder.decodeQueueSize ?? 0);
        if (backlog >= MAX_PENDING) {
            // Falling behind. Dropping a delta frame causes a brief artefact that
            // the next key frame clears; queueing instead would add latency that
            // never recovers.
            if (!key) {
                this.stats.framesDropped++;
                return;
            }
        }
        try {
            const chunk = new EncodedVideoChunk({
                type: key ? "key" : "delta",
                // WebCodecs timestamps are microseconds.
                timestamp: Number(pts) * 1000,
                data,
            });
            this.#pending++;
            decoder.decode(chunk);
        }
        catch (err) {
            this.#pending = Math.max(0, this.#pending - 1);
            this.stats.framesDropped++;
            this.#sawKeyFrame = false;
            this.#requestRefresh();
        }
    }
    #requestRefresh() {
        if (this.#refreshRequested)
            return;
        this.#refreshRequested = true;
        this.#events.onNeedRefresh?.();
        // Allow another request if the refresh does not arrive.
        setTimeout(() => {
            this.#refreshRequested = false;
        }, 1000);
    }
    #paint(frame) {
        this.#pending = Math.max(0, this.#pending - 1);
        try {
            if (this.#closed)
                return;
            const w = frame.displayWidth || frame.codedWidth;
            const h = frame.displayHeight || frame.codedHeight;
            if (w && h && (this.#canvas.width !== w || this.#canvas.height !== h)) {
                this.#canvas.width = w;
                this.#canvas.height = h;
                this.stats.width = w;
                this.stats.height = h;
                this.#events.onResize?.(w, h);
            }
            this.#ctx?.drawImage(frame, 0, 0);
            const first = this.stats.framesDecoded === 0;
            this.stats.framesDecoded++;
            const now = performance.now();
            this.stats.lastFrameAt = now;
            this.#fpsWindow.push(now);
            // Keep a one-second window for a stable fps reading.
            while (this.#fpsWindow.length && now - this.#fpsWindow[0] > 1000) {
                this.#fpsWindow.shift();
            }
            this.stats.fps = this.#fpsWindow.length;
            if (first)
                this.#events.onFirstFrame?.();
        }
        finally {
            // VideoFrame holds GPU memory; not closing it stalls the decoder.
            frame.close();
        }
    }
    #teardownDecoder() {
        const d = this.#decoder;
        this.#decoder = null;
        this.#pending = 0;
        this.#codec = null;
        if (!d)
            return;
        try {
            if (d.state !== "closed")
                d.close();
        }
        catch {
            /* already closed */
        }
    }
    close() {
        this.#closed = true;
        this.#teardownDecoder();
    }
}
